import { useMemo, useState, useEffect } from 'react';
import { OPENINGS_DATA, OPENING_EXERCISES, getExercisesByTag, getExercisesByOpening } from '@chess-platform/shared';

export function useOpenings() {
    const [rawData, setRawData] = useState<any[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);

    // Lazy load the 3.4MB dataset
    useEffect(() => {
        let isMounted = true;
        import('@chess-platform/shared/src/data/openings-large.json')
            .then((module) => {
                const data = module.default;
                if (isMounted) {
                    if (Array.isArray(data)) {
                        setRawData(data);
                    }
                    setIsLoaded(true);
                }
            })
            .catch((err) => {
                console.error('Failed to load openings-large.json:', err);
                if (isMounted) setIsLoaded(true);
            });
        return () => { isMounted = false; };
    }, []);

    // coreOpenings are the 24 basic ones - Clean Strings
    const coreOpenings = useMemo(() => {
        try {
            if (!Array.isArray(OPENINGS_DATA)) return [];
            const flat: any[] = [];
            OPENINGS_DATA.forEach((group: any) => {
                if (!group || !Array.isArray(group.items)) return;
                group.items.forEach((item: any) => {
                    if (!item) return;
                    const main = {
                        eco: String(item.eco || '---'),
                        name: String(item.name || 'Apertura'),
                        fenRoot: String(item.fenRoot || '')
                    };
                    flat.push(main);
                    if (Array.isArray(item.variants)) {
                        item.variants.forEach((v: any) => {
                            flat.push({
                                eco: String(v.eco || main.eco),
                                name: String(v.name || 'Variante'),
                                fenRoot: String(v.fenRoot || main.fenRoot)
                            });
                        });
                    }
                });
            });
            return flat;
        } catch (e) {
            console.error('Error in coreOpenings memo:', e);
            return [];
        }
    }, []);

    // allOpeningsFlat for search functionality
    const allOpeningsFlat = useMemo(() => {
        const flat: any[] = [];
        
        // Always include core openings first (they are already in our memory)
        if (Array.isArray(coreOpenings)) {
            coreOpenings.forEach((op: any) => {
                flat.push({
                    eco: String(op.eco || '---'),
                    name: String(op.name || 'Apertura'),
                    fenRoot: String(op.fenRoot || ''),
                    source: 'core'
                });
            });
        }

        // Add the thousands of openings from rawData (large JSON) if loaded
        if (Array.isArray(rawData) && rawData.length > 0) {
            try {
                // The large JSON is a FLAT array of opening objects, not nested groups
                rawData.forEach((item: any) => {
                    if (!item || !item.name) return;
                    
                    // Simple duplicate check (ECO + Name)
                    const isDup = flat.some(f => f.eco === item.eco && f.name === item.name);
                    if (!isDup) {
                        flat.push({
                            eco: String(item.eco || '---'),
                            name: String(item.name || 'Apertura'),
                            fenRoot: String(item.fenRoot || ''),
                            tag: String(item.tag || ''),
                            source: 'large'
                        });
                    }
                });
            } catch (e) {
                console.error('Error flattening rawData:', e);
            }
        }
        
        console.log('--- useOpenings: Final Flatted DB ---', { 
            total: flat.length, 
            core: coreOpenings?.length || 0,
            large: Array.isArray(rawData) ? rawData.length : 0
        });
        
        return flat;
    }, [rawData, coreOpenings]);

    const exercises = useMemo(() => OPENING_EXERCISES || [], []);

    const searchOpenings = (query: string) => {
        const q = String(query || '').toLowerCase().trim();
        if (!q) return [];
        return allOpeningsFlat.filter((item: any) =>
            item.name.toLowerCase().includes(q) ||
            item.eco.toLowerCase().includes(q)
        );
    };

    return {
        coreOpenings,
        allOpenings: rawData,
        allOpeningsFlat,
        exercises,
        isLoaded,
        searchOpenings,
        getExercisesByTag,
        getExercisesByOpening
    };
}
